import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useModals } from '@/stores/modals/modals';
import { useAppAuthData } from '@/stores/app/authData';
import { useHighlights } from '@/stores/highlights/highlights';
import { useDocumentEditor } from '@/stores/document/editor';
import { useEditor } from '@/stores/editor/editor';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { useJobProfiles } from '@/stores/jobProfiles/jobProfiles';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import { buildRequiredFieldGuidanceMessages } from '@/helpers/jobProfileGuidanceMessages';
import {
  getJobProfile, getJobProfileRevisions, getJobProfileVersions, getJobProfileHris,
  rescoreJobProfile, finalizeJobProfileTemplate, applyJobProfileTemplate, getProfilesByTemplate,
  deleteJobProfile as deleteJobProfileRequest, restoreJobProfileRevision, duplicateJobProfile,
  linkHrisProfile as linkHrisProfileRequest, renameJobProfileRevision,
  grantDraftProfileAccess,
  revokeDraftProfileAccess,
  updateProfileMemo,
} from '@/api/jobProfile';
import { generateJobProfileSummary, generateJobProfile } from '@/api/dfd';
import { useTracking } from '@/stores/tracking/tracking';

export const useCurrentJobProfile = defineStore('currentJobProfile', () => {
  const { setLoading } = useAppLoading();
  const { checkRbacPermissions } = useAppAuthData();
  const { finalizedTemplates } = storeToRefs(useJobProfiles());

  const isRescoring = ref(false);
  const rescorePending = ref(false);
  const isUpdated = ref(false);
  const jobProfile = ref(null);
  const isHrisProfile = ref(false);
  const editorContents = ref({});

  // ignore content updates for 1 second after a load
  const rescoreCoolDown = ref(true);

  const profileRevisions = ref([]);
  const sideRevision = ref(null);
  const sideRevisionLoading = ref(false);
  const templateProfiles = ref([]);
  const finalizedLockedFieldsHash = ref(null);

  const startRescoreCoolDown = () => {
    rescoreCoolDown.value = true;
    setTimeout(() => {
      rescoreCoolDown.value = false;
    }, 1000);
  };

  let rescoreTimer = null;

  const clearRescoreTimer = () => {
    clearTimeout(rescoreTimer);
    rescoreTimer = null;
    rescorePending.value = false;
  };

  const resetJobProfile = () => {
    clearRescoreTimer();

    jobProfile.value = null;
    isHrisProfile.value = false;
    templateProfiles.value = [];
    finalizedLockedFieldsHash.value = null;
    profileRevisions.value = [];
    editorContents.value = {};
    isUpdated.value = false;
    isRescoring.value = false;
    rescorePending.value = false;
    startRescoreCoolDown();
  };

  const toLegacyFields = (fields = []) => fields.reduce((res, field) => {
    if (field.header && field.header.length > 0) {
      res.push({
        id: `${field.type}__header`,
        label: '',
        sections: [{ blocks: field.header }],
      });
    }
    res.push({
      id: field.type,
      label: field.label,
      sections: field.sections || [{ blocks: field.blocks }],
    });
    if (field.footer && field.footer.length > 0) {
      res.push({
        id: `${field.type}__footer`,
        label: '',
        sections: [{ blocks: field.footer }],
      });
    }
    return res;
  }, []);

  const loadJobProfile = async ({ ulid, isHris = false, rescore = false }) => {
    if (!ulid) {
      resetJobProfile();
      return;
    }

    clearRescoreTimer();
    setLoading({ pageLoading: true });
    useHighlights().updateBodyMessages({});
    startRescoreCoolDown();

    try {
      const profile = await (isHris ? getJobProfileHris : getJobProfile)({ ulid, rescore });
      jobProfile.value = profile;
      isUpdated.value = false;
      isHrisProfile.value = isHris;

      if (profile.last_applied_template_ulid) {
        await useJobProfiles().loadFinalizedProfileTemplates();
      }

      useHighlights().updateBodyMessages({
        bodyMessages: profile.messages,
        fields: toLegacyFields(profile.fields),
        title: profile.title,
        location: profile.location,
      });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile load error: ${err.message || err}`,
      });
      jobProfile.value = null;
      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const isTemplate = computed(() => jobProfile.value?.type_key === 'job_profile_template');

  const hasEditAccess = computed(() => jobProfile.value?.rbac?.editOwnDraftProfile);

  const hasPendingApprovalFlows = computed(() => jobProfile.value?.approval_flow_counts?.pending > 0);
  const showPendingApprovalAlert = computed(() => hasPendingApprovalFlows.value
    && (checkRbacPermissions(['saveDraftProfileRevision']) || hasEditAccess.value));

  const readOnlyDraft = computed(() => !isHrisProfile.value
    && !checkRbacPermissions(['saveDraftProfileRevision'])
    && !hasEditAccess.value);
  const readOnlyTemplate = computed(() => isTemplate.value && !checkRbacPermissions(['createProfileTemplate']));
  const readOnly = computed(() => readOnlyDraft.value
    || readOnlyTemplate.value
    || isHrisProfile.value
    || hasPendingApprovalFlows.value);

  const appliedTemplate = computed(() => finalizedTemplates.value.find(
    (t) => t.ulid === jobProfile.value?.last_applied_template_ulid,
  ));

  const isAppliedTemplateOutdated = computed(() => (
    !!appliedTemplate.value
    && jobProfile.value?.locked_fields_hash !== appliedTemplate.value.locked_fields_hash
  ));

  const fields = computed(() => {
    const jobFields = jobProfile.value?.fields || [];

    return jobFields.map((field) => {
      const blocks = typeof field.blocks === 'string' ? [{
        type: 'paragraph',
        html: field.blocks,
      }] : field.blocks;

      const headerBlocks = Array.isArray(field.header) ? field.header : [];
      const footerBlocks = Array.isArray(field.footer) ? field.footer : [];

      const nodes = field.formatBlacklist.reduce((res, tag) => ({ ...res, [tag]: true }), {});

      return {
        ...field,
        id: field.id || field.type,
        blocks,
        html: blocks?.map((block) => block.html).join(''),
        headerHtml: headerBlocks.map((b) => b.html).join(''),
        footerHtml: footerBlocks.map((b) => b.html).join(''),
        disabledNodes: {
          paragraph: nodes.P,
          heading: nodes.H1 && nodes.H2 && nodes.H3,
          h1: nodes.H1,
          h2: nodes.H2,
          h3: nodes.H3,
          list: nodes.UL,
          orderedList: nodes.OL,
          link: nodes.A,
          bold: nodes.STRONG,
          italic: nodes.EM,
        },
      };
    });
  });

  const summaryField = computed(() => fields.value?.find((f) => f.content_generator_type === 'summary') ?? null);
  const summaryFieldId = computed(() => summaryField.value?.id ?? null);

  const nonEmptyFields = computed(() => fields.value.filter(
    (field) => field.blocks?.some((block) => block.html?.trim?.()),
  ));

  const getFieldHtml = (key) => {
    const field = editorContents.value?.[key] || {};
    return [field.header, field.content, field.footer].join('');
  };

  const bodyMessages = computed(() => {
    const profileMessages = jobProfile.value?.bodyMessages || {};
    return profileMessages;
  });

  const loadRevisions = async () => {
    if (!jobProfile.value?.ulid) return;

    try {
      setLoading({ listLoading: true });
      if (isHrisProfile.value) {
        const data = await getJobProfileVersions({ ulid: jobProfile.value.ulid });
        profileRevisions.value = (data?.versions || []).map((version) => ({
          ...version,
          created: version.effective_date,
        }));
      } else {
        const data = await getJobProfileRevisions({ ulid: jobProfile.value.ulid });
        profileRevisions.value = data?.revisions || [];
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile revisions load error: ${err.message || err}`,
      });
      profileRevisions.value = [];
      throw err;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const loadFinalizedTemplateHash = async () => {
    if (!jobProfile.value?.ulid) return;

    try {
      const profile = await getJobProfile({
        ulid: jobProfile.value.ulid,
        rescore: false,
        finalized: true,
      });

      finalizedLockedFieldsHash.value = profile?.locked_fields_hash || null;
    } catch (err) {
      finalizedLockedFieldsHash.value = null;
      throw err;
    }
  };

  const loadProfilesByTemplate = async () => {
    if (!jobProfile.value?.ulid) return;

    try {
      setLoading({ listLoading: true });
      const data = await getProfilesByTemplate({ ulid: jobProfile.value.ulid });
      templateProfiles.value = data?.profiles || [];

      await loadFinalizedTemplateHash();
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Template profiles load error: ${err.message || err}`,
      });
      templateProfiles.value = [];
      finalizedLockedFieldsHash.value = null;
      throw err;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const revisions = computed(() => {
    const today = new Date().toLocaleDateString();
    const revisionList = profileRevisions.value.filter((revision, i) => {
      if (i === 0) return true;

      const revisionDay = revision.created ? new Date(revision.created).toLocaleDateString() : null;
      if (revisionDay === today) return true;
      if (revision.revision_name) return true;

      const prevRevision = profileRevisions.value[i - 1];
      const prevDay = prevRevision?.created ? new Date(prevRevision.created).toLocaleDateString() : null;
      if (revision.createdBy !== prevRevision.createdBy) return true;
      if (revisionDay !== prevDay) return true;

      return false;
    });

    const seenQuarters = new Set();
    return revisionList
      .map((revision) => {
        let quarterLabel = '';
        let dateString = '';
        if (revision.created) {
          const date = new Date(revision.created);
          dateString = date.toLocaleString();
          const year = date.getFullYear();
          const quarter = Math.ceil((date.getMonth() + 1) / 3);

          if (!seenQuarters.has(`${year}-Q${quarter}`)) {
            quarterLabel = `Q${quarter} ${year}`;
            seenQuarters.add(`${year}-Q${quarter}`);
          }
        }
        return {
          ...revision,
          id: revision.ulid,
          created: dateString,
          ...(quarterLabel && { quarter: quarterLabel }),
        };
      });
  });

  const rescorePayload = computed(() => {
    const profile = jobProfile.value;
    if (!profile) return null;

    const contents = editorContents.value;

    const fieldsMap = {};
    fields.value.forEach((field) => {
      const entry = contents[field.id] || {};
      fieldsMap[field.id] = entry.content || field.html;

      if (entry.header || field.headerHtml) {
        fieldsMap[`${field.id}__header`] = entry.header || field.headerHtml;
      }
      if (entry.footer || field.footerHtml) {
        fieldsMap[`${field.id}__footer`] = entry.footer || field.footerHtml;
      }
    });

    return {
      title: contents.title?.content || 'Untitled',
      fields: fieldsMap,
      jobCode: contents.jobCode?.content,
      company: contents.company?.content,
      location: contents.location?.content,
      jobLevelUlid: jobProfile.value?.job_level_ulid,
    };
  });

  const doRescore = async () => {
    isRescoring.value = true;
    const rescoreStart = Date.now();

    try {
      const profile = await rescoreJobProfile({
        ulid: jobProfile.value?.ulid,
        payload: rescorePayload.value,
      });

      jobProfile.value = { ...profile, access_list: jobProfile.value?.access_list || [] };

      useTracking().jpEditorRescore({
        ulid: profile?.ulid,
        revisionUlid: profile?.last_revision_ulid,
        duration: Date.now() - rescoreStart,
      });

      useHighlights().updateBodyMessages({
        bodyMessages: profile.messages,
        fields: toLegacyFields(profile.fields),
        title: profile.title,
        location: profile.location,
        isRescore: true,
      });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Job Profile rescore error: ${err.message || err}`,
      });
      throw err;
    } finally {
      rescorePending.value = false;
      isRescoring.value = false;
    }
  };

  const scheduleRescore = () => {
    if (isHrisProfile.value || rescoreCoolDown.value) {
      return;
    }

    rescorePending.value = true;

    clearTimeout(rescoreTimer);
    rescoreTimer = setTimeout(doRescore, 2000);
  };

  const updateEditorContent = ({ editorId, part = 'content', html }) => {
    let baseId = editorId;
    let fieldPart = part;
    if (editorId.endsWith('__header') || editorId.endsWith('__footer')) {
      [baseId, fieldPart] = editorId.split('__');
    }

    const prev = editorContents.value[baseId];
    const prevObj = (prev && typeof prev === 'object') ? prev : {};

    // if the content is the same, don't rescore
    if (prevObj[fieldPart] === html) {
      return;
    }

    editorContents.value = { ...editorContents.value, [baseId]: { ...prevObj, [fieldPart]: html } };
    scheduleRescore();
  };

  const updateJobLevelUlid = (ulid) => {
    jobProfile.value.job_level_ulid = ulid;
    scheduleRescore();
  };

  const generateProfileJobDescription = (mode = 'replace', selectedFieldIds = null) => {
    const jobDescriptionField = fields.value.find((f) => f.is_job_description);
    if (!jobDescriptionField) return;

    const combined = fields.value
      .filter((f) => !f.is_job_description)
      .filter((f) => !selectedFieldIds || selectedFieldIds.includes(f.id))
      .map((f) => {
        const editor = editorContents.value[f.id] || {};
        const header = editor.header || '';
        const content = editor.content || '';
        const footer = editor.footer || '';
        const parts = [header, content, footer].filter((s) => !isEmptyHtml(s));

        if (!parts.length) return '';
        return [`<h3>${f.label}</h3>`, ...parts].join('\n');
      })
      .filter(Boolean)
      .join('\n');

    const tiptap = useDocumentEditor().tiptap[jobDescriptionField.id];
    if (tiptap) {
      const html = mode === 'append'
        ? `${tiptap.getHTML()}\n${combined}`
        : combined;
      tiptap.chain()
        .setMeta('addToHistory', false)
        .setContent(html, {
          emitUpdate: true,
          parseOptions: { preserveWhitespace: true },
        })
        .run();
      setTimeout(() => { tiptap.emit('update', {}); }, 100);
    }
  };

  const generateFieldContent = async (field) => {
    const isSummary = field.content_generator_type === 'summary';

    const firstDraft = useFirstDraft();
    firstDraft.isNewContentLoading = true;
    firstDraft.newContentLoadingStatus = isSummary ? 'Generating summary...' : 'Generating content...';
    try {
      const { summary, fieldType, profile = {} } = isSummary
        ? await generateJobProfileSummary(jobProfile.value?.ulid)
        : await generateJobProfile(
          jobProfile.value?.ulid,
          { useExistingSummary: !isEmptyHtml(getFieldHtml(summaryFieldId.value)) },
        );

      const targetId = fieldType || field.id;
      const content = isSummary ? summary : profile[targetId];

      const tiptap = useDocumentEditor().tiptap[targetId];
      if (tiptap) {
        tiptap.chain()
          .setMeta('addToHistory', false)
          .setContent(content, {
            emitUpdate: true,
            parseOptions: { preserveWhitespace: true },
          })
          .run();
        setTimeout(() => { tiptap.emit('update', {}); }, 100);
      }
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to generate content: ${err.message || err}` });
      throw err;
    } finally {
      firstDraft.isNewContentLoading = false;
      firstDraft.newContentLoadingStatus = '';
    }
  };

  const generateSummaryContent = () => {
    if (summaryField.value) generateFieldContent(summaryField.value);
  };

  const isAlreadyFinalized = computed(() => (
    jobProfile.value?.finalized_revision_ulid === jobProfile.value?.last_revision_ulid
  ));

  const profileGuidanceMessages = computed(() => {
    const templateMessage = {
      severity_class: 'red-underline',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Apply Template',
      tooltip_long: 'Use a levelling template to get started',
      suggestions: [],
    };
    const templateUpdateMessage = {
      severity_class: 'red-underline',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Update Template',
      tooltip_long: 'The template associated with this profile has a published update.',
      suggestions: [],
    };
    const templateKudoMessage = {
      severity_class: 'green-solid',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Template Applied',
      suggestions: [],
    };

    const titleKudoMessage = {
      severity_class: 'green-solid',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Job Title Added',
      suggestions: [],
    };

    const missingJobCodeMessage = {
      severity_class: 'red-underline',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Add a Job Code',
      suggestions: [],
    };
    const missingJobCodeKudoMessage = {
      severity_class: 'green-solid',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Job Code Added',
      suggestions: [],
    };

    const jobCodeMismatchMessage = {
      severity_class: 'red-underline',
      action: 'custom',
      is_nudge: false,
      tooltip_short: 'Job code does not match Workday source',
      suggestions: [],
    };

    const templateGuidanceMessages = {};
    if (!jobProfile.value?.last_applied_template_ulid) {
      templateGuidanceMessages.guidance_template = templateMessage;
    }
    if (jobProfile.value?.last_applied_template_ulid && !isAppliedTemplateOutdated.value) {
      templateGuidanceMessages.guidance_template_kudo = templateKudoMessage;
    }
    if (isAppliedTemplateOutdated.value) {
      templateGuidanceMessages.guidance_template_update = templateUpdateMessage;
    }

    const jobCodeMismatch = jobProfile.value?.hris_reference
      && jobProfile.value?.job_code
      && jobProfile.value?.hris_reference?.job_code !== jobProfile.value?.job_code;

    return {
      ...jobProfile.value?.bodyMessages,

      ...(!isTemplate.value && !isHrisProfile.value && {
        ...templateGuidanceMessages,
      }),
      ...(jobProfile.value?.title && { guidance_title: titleKudoMessage }),
      ...buildRequiredFieldGuidanceMessages(fields.value, getFieldHtml, isEmptyHtml),
      ...(!isTemplate.value && {
        guidance_job_code: !jobProfile.value?.job_code ? missingJobCodeMessage : missingJobCodeKudoMessage,
      }),
      ...(jobCodeMismatch ? { guidance_job_code_mismatch: jobCodeMismatchMessage } : {}),
    };
  });

  const finalizeTemplate = async () => {
    setLoading({ modalLoading: true });
    try {
      const { template } = await finalizeJobProfileTemplate({ ulid: jobProfile.value?.ulid });
      jobProfile.value = template;

      await loadFinalizedTemplateHash();
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Finalize template error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const applyTemplate = async ({ templateUlid }) => {
    setLoading({ modalLoading: true });
    try {
      const {
        document_ulid: documentUlid, revision_ulid: revisionUlid,
      } = await applyJobProfileTemplate({ ulid: jobProfile.value?.ulid, templateUlid });

      return { documentUlid, revisionUlid, oldRevisionUlid: jobProfile.value?.last_revision_ulid };
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Apply template error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  // Apply the open template onto another profile without modal loading state.
  const applyTemplateToProfile = async ({ profileUlid }) => {
    if (!profileUlid || !jobProfile.value?.ulid) {
      throw new Error('Profile and template are required');
    }

    return applyJobProfileTemplate({
      ulid: profileUlid,
      templateUlid: jobProfile.value.ulid,
    });
  };

  const deleteJobProfile = async () => {
    const ulid = jobProfile.value?.ulid;
    if (!ulid) {
      return;
    }

    setLoading({ pageLoading: true });
    try {
      await deleteJobProfileRequest({ ulid });
      useAppSnackbar().addSuccessSnackbar({
        text: `${jobProfile.value?.title || 'Job'} has been deleted`,
      });
      resetJobProfile();
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Delete job profile error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const createDraftFromHrisProfile = async () => {
    setLoading({ modalLoading: true });
    try {
      const { document_ulid: documentUlid } = await duplicateJobProfile({ ulid: jobProfile.value?.ulid });
      return documentUlid;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to create draft: ${err.message || err}` });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const restoreRevision = async ({ revisionUlid: explicitRevisionUlid } = {}) => {
    const ulid = jobProfile.value?.ulid;
    const revisionUlid = explicitRevisionUlid || sideRevision.value?.revision_ulid;
    if (!ulid || !revisionUlid) {
      return;
    }
    const revision = revisions.value.find((r) => r.ulid === revisionUlid);
    const revisionName = revision ? `Restored revision of ${revision.created}` : '';

    setLoading({ pageLoading: true });
    try {
      const { profile = {} } = await restoreJobProfileRevision({ ulid, revisionUlid, revisionName });
      jobProfile.value = { ...profile, access_list: jobProfile.value?.access_list || [] };
      useHighlights().updateBodyMessages({
        bodyMessages: profile.messages,
        fields: toLegacyFields(profile.fields),
        title: profile.title,
        location: profile.location,
      });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Restore revision error: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const renameRevision = async ({ revisionUlid, name }) => {
    setLoading({ listLoading: true });
    try {
      const { revision } = await renameJobProfileRevision({ ulid: jobProfile.value.ulid, revisionUlid, name });
      const rev = profileRevisions.value.find((r) => r.ulid === revisionUlid);
      if (rev) rev.revision_name = revision?.revision_name ?? null;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Rename revision error: ${err.message || err}` });
      throw err;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const linkHrisProfile = async (hrisUlid) => {
    setLoading({ modalLoading: true });
    try {
      await linkHrisProfileRequest({
        draftUlid: jobProfile.value.ulid,
        hrisUlid,
      });
      const updated = await getJobProfile({ ulid: jobProfile.value.ulid, rescore: true });
      jobProfile.value.hris_matching = updated.hris_matching;
      jobProfile.value.hris_reference = updated.hris_reference;
      jobProfile.value.hris_reference_ulid = updated.hris_reference_ulid;
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Failed to link to HRIS profile: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const updateMemo = async (memo) => {
    try {
      const result = await updateProfileMemo({ ulid: jobProfile.value.ulid, memo });
      jobProfile.value.memo = result.memo ?? '';
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to update memo: ${err.message || err}` });
      throw err;
    }
  };

  const unlinkDraftProfile = async (draftUlid) => {
    setLoading({ modalLoading: true });
    try {
      await linkHrisProfileRequest({ draftUlid, hrisUlid: null });
      const updated = await getJobProfileHris({ ulid: jobProfile.value.ulid, rescore: true });
      jobProfile.value.draft_profiles = updated.draft_profiles ?? [];
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({
        text: `Failed to unlink draft profile: ${err.message || err}`,
      });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const copyAllFieldsToClipboard = (title) => {
    const content = fields.value.map((field) => {
      const fieldLabel = `<h2>${field.label}</h2>`;
      const fieldHeader = isEmptyHtml(field.headerHtml) ? '' : field.headerHtml;
      const fieldBody = isEmptyHtml(field.html) ? '' : field.html;
      const fieldFooter = isEmptyHtml(field.footerHtml) ? '' : field.footerHtml;
      const fieldContent = `${fieldHeader}${fieldBody}${fieldFooter}`;
      return fieldContent.length ? `${fieldLabel}${fieldContent}` : '';
    });

    const fullContent = `<h1>${title}</h1>${content.join('')}`;

    useEditor().copyToClipboard({ content: fullContent, isHtml: true });
  };

  const grantAccess = async (userUlid) => {
    setLoading({ modalLoading: true });
    try {
      const { access_list: newAccessList } = await grantDraftProfileAccess({
        draftUlid: jobProfile.value.ulid,
        userUlid,
      });
      jobProfile.value = { ...jobProfile.value, access_list: newAccessList || [] };
      useModals().setModal({ field: 'grantAccess' });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to grant access: ${err.message || err}` });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const revokeAccess = async (userUlid) => {
    setLoading({ modalLoading: true });
    try {
      const { access_list: newAccessList } = await revokeDraftProfileAccess({
        draftUlid: jobProfile.value.ulid,
        userUlid,
      });
      jobProfile.value = { ...jobProfile.value, access_list: newAccessList || [] };
      useModals().setModal({ field: 'revokeAccess' });
    } catch (err) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to revoke access: ${err.message || err}` });
      throw err;
    } finally {
      setLoading({ modalLoading: false });
    }
  };

  const updateApprovalFlowCounts = (flows) => {
    jobProfile.value.approval_flow_counts = flows.reduce((acc, flow) => {
      acc[flow.status] = (acc[flow.status] || 0) + 1;
      return acc;
    }, {});
  };

  return {
    // State
    isRescoring,
    rescorePending,
    isUpdated,
    jobProfile,
    fields,
    summaryField,
    summaryFieldId,
    nonEmptyFields,
    bodyMessages,
    profileGuidanceMessages,
    revisions,
    templateProfiles,
    finalizedLockedFieldsHash,
    isHrisProfile,
    isTemplate,
    isAlreadyFinalized,
    editorContents,
    sideRevision,
    sideRevisionLoading,
    readOnly,
    readOnlyTemplate,
    readOnlyDraft,
    hasPendingApprovalFlows,
    showPendingApprovalAlert,
    hasEditAccess,
    appliedTemplate,
    isAppliedTemplateOutdated,

    // Actions
    loadJobProfile,
    resetJobProfile,
    loadRevisions,
    loadProfilesByTemplate,
    updateEditorContent,
    finalizeTemplate,
    applyTemplate,
    applyTemplateToProfile,
    deleteJobProfile,
    generateProfileJobDescription,
    generateFieldContent,
    generateSummaryContent,
    restoreRevision,
    renameRevision,
    createDraftFromHrisProfile,
    updateJobLevelUlid,
    linkHrisProfile,
    updateMemo,
    unlinkDraftProfile,
    copyAllFieldsToClipboard,
    grantAccess,
    revokeAccess,
    updateApprovalFlowCounts,
  };
});
